import i18n, { getStripeLanguage } from '../../../../i18n'

export const UPSELL_FEATURED_PLAN_TIER = 3
export const UPSELL_FEATURED_PACKAGE_CODE = 'addon_25_artes'
export const UPSELL_CAMPAIGN_PACKAGE_CODE = 'addon_10_artes'
export const UPSELL_DOWNLOAD_PACKAGE_CODE = 'addon_download'

function isBrazilianLanguage(language = getStripeLanguage()) {
  const normalized = String(language || '').toLowerCase()
  return normalized === 'ptbr' || normalized.startsWith('pt')
}

function usesBrazilianCurrency({ language, currency } = {}) {
  const normalizedCurrency = String(currency || '').toLowerCase()
  if (normalizedCurrency) {
    return normalizedCurrency === 'brl'
  }

  return isBrazilianLanguage(language)
}

export function formatUpsellPrice(price, language = getStripeLanguage(), currency) {
  const planValue = parseFloat(price)
  const useBrazil = usesBrazilianCurrency({ language, currency })

  return planValue.toLocaleString(useBrazil ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency: useBrazil ? 'BRL' : 'USD',
  })
}

export function formatUpsellStaticCurrency(value, language = getStripeLanguage(), currency) {
  const useBrazil = usesBrazilianCurrency({ language, currency })

  return Number(value).toLocaleString(useBrazil ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency: useBrazil ? 'BRL' : 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function getPlanArtsQuantity(plan) {
  if (!plan?.PlanItems) return null

  const item = plan.PlanItems.find(
    (planItem) =>
      planItem.type === 'single_post_quantity' ||
      planItem.type === 'art_generation_count',
  )

  return item?.quantity ?? null
}

export function getPlanByTier(dbPlans, tier) {
  return dbPlans.find((plan) => plan.tier === tier) ?? null
}

export function getPackageByCode(code, packages = []) {
  return packages.find((pkg) => pkg.code === code) ?? null
}

export function getPackagePriceValue(pkg) {
  if (typeof pkg?.price !== 'number') {
    return null
  }

  return pkg.price
}

export function getPackagePrice(pkg, language = getStripeLanguage()) {
  const priceValue = getPackagePriceValue(pkg)
  if (priceValue == null) return null

  return formatUpsellStaticCurrency(priceValue, language, pkg?.currency)
}

export function getCurrentPlanTier(activeSubscription) {
  return (
    activeSubscription?.Plan?.tier ??
    activeSubscription?.plan?.tier ??
    0
  )
}

export function getUpgradePlans(dbPlans, currentTier = 0) {
  return dbPlans
    .filter((plan) => plan.tier > 0 && plan.tier > currentTier)
    .sort((a, b) => a.tier - b.tier)
}

export function getSelectablePlans(dbPlans, currentTier = 0) {
  return dbPlans
    .filter((plan) => plan.tier > 0 && plan.tier !== currentTier)
    .sort((a, b) => a.tier - b.tier)
}

export function getNextUpgradePlan(dbPlans, currentTier = 0) {
  return getUpgradePlans(dbPlans, currentTier)[0] ?? null
}

export function getFeaturedUpgradePlan(dbPlans, currentTier = 0) {
  if (UPSELL_FEATURED_PLAN_TIER > currentTier) {
    const preferredPlan = getPlanByTier(dbPlans, UPSELL_FEATURED_PLAN_TIER)
    if (preferredPlan) {
      return preferredPlan
    }
  }

  return getNextUpgradePlan(dbPlans, currentTier)
}

export function mapUpgradePlansToOptions(
  dbPlans,
  currentTier = 0,
  { language = getStripeLanguage(), excludeTiers = [] } = {},
) {
  return getSelectablePlans(dbPlans, currentTier)
    .filter((plan) => !excludeTiers.includes(plan.tier))
    .map((plan) => mapPlanToOption(plan, language))
}

export function filterStaticPlanOptions(
  options,
  currentTier = 0,
  excludeTier = null,
) {
  return options.filter((option) => {
    if (option.tier == null) {
      return false
    }

    return option.tier !== currentTier && option.tier !== excludeTier
  })
}

export function mapPlanToOption(plan, language = getStripeLanguage()) {
  const arts = getPlanArtsQuantity(plan)
  const artsLabel = arts != null
    ? i18n.t('upsell.arts_per_month', { count: arts })
    : null

  return {
    id: `tier-${plan.tier}`,
    tier: plan.tier,
    label: artsLabel ? `${plan.description} · ${artsLabel}` : plan.description,
    price: `${formatUpsellPrice(plan.price, language)} ${i18n.t('upsell.per_month')}`,
    plan,
  }
}

export function mapPlansToOptions(dbPlans, { language = getStripeLanguage(), excludeTiers = [] } = {}) {
  return dbPlans
    .filter((plan) => plan.tier > 0 && !excludeTiers.includes(plan.tier))
    .sort((a, b) => a.tier - b.tier)
    .map((plan) => mapPlanToOption(plan, language))
}

export function mapPackageToOption(pkg, language = getStripeLanguage()) {
  return {
    id: pkg.code,
    code: pkg.code,
    arts: pkg.arts,
    label: pkg.title || i18n.t('upsell.arts_count', { count: pkg.arts }),
    price: getPackagePrice(pkg, language),
    package: pkg,
  }
}

export function mapPackagesToOptions({
  language = getStripeLanguage(),
  excludeCodes = [],
  packages = [],
} = {}) {
  return packages
    .filter((pkg) => !excludeCodes.includes(pkg.code))
    .map((pkg) => mapPackageToOption(pkg, language))
}

export function mapPackageToRechargeOption(pkg, language = getStripeLanguage()) {
  return {
    id: `recharge-${pkg.code}`,
    code: pkg.code,
    arts: pkg.arts,
    label: `${i18n.t('upsell.recharge_label')} · ${i18n.t('upsell.arts_count', { count: pkg.arts })}`,
    price: getPackagePrice(pkg, language),
    package: pkg,
  }
}

export function buildFeaturedPlanOffer(plan, language = getStripeLanguage()) {
  if (!plan) return null

  const arts = getPlanArtsQuantity(plan)
  const formattedPrice = formatUpsellPrice(plan.price, language)
  const unitPrice = arts
    ? i18n.t('upsell.per_art', { price: formatUpsellPrice(plan.price / arts, language) })
    : undefined

  return {
    label: arts
      ? i18n.t('upsell.plan_with_arts', {
        plan: plan.description,
        count: arts,
      })
      : i18n.t('upsell.plan_label', { plan: plan.description }),
    price: `${formattedPrice}${i18n.t('upsell.per_month')}`,
    unitPrice,
    description: i18n.t('upsell.plan_description'),
    plan,
    tier: plan.tier,
    name: plan.description,
    artsPerMonth: arts,
  }
}

export function buildFeaturedPackageOffer(pkg, language = getStripeLanguage(), options = {}) {
  if (!pkg) return null

  const price = getPackagePrice(pkg, language)
  const priceValue = getPackagePriceValue(pkg)
  const unitPrice =
    pkg.arts && priceValue != null
      ? i18n.t('upsell.per_art', {
        price: formatUpsellPrice(priceValue / pkg.arts, language),
      })
      : undefined

  return {
    label: options.label ?? i18n.t('upsell.package_with_arts', { count: pkg.arts }),
    price,
    unitPrice,
    description: options.description,
    subdescription: options.subdescription ?? i18n.t('upsell.package_subdescription'),
    package: pkg,
    code: pkg.code,
    arts: pkg.arts,
  }
}

export function buildRechargeFeaturedOffer(pkg, language = getStripeLanguage()) {
  if (!pkg) return null

  return {
    label: i18n.t('upsell.recharge_label'),
    price: getPackagePrice(pkg, language),
    description: i18n.t('upsell.arts_count', { count: pkg.arts }),
    subdescription: i18n.t('upsell.recharge_subdescription'),
    package: pkg,
    code: pkg.code,
    arts: pkg.arts,
  }
}

export function buildUpgradeFeaturedOffer(plan, language = getStripeLanguage()) {
  if (!plan) return null

  const arts = getPlanArtsQuantity(plan)
  const formattedPrice = formatUpsellPrice(plan.price, language)
  const unitPrice = arts
    ? i18n.t('upsell.per_art', { price: formatUpsellPrice(plan.price / arts, language) })
    : undefined

  return {
    label: arts
      ? i18n.t('upsell.upgrade_plan_with_arts', {
        plan: plan.description,
        count: arts,
      })
      : i18n.t('upsell.upgrade_plan_label', { plan: plan.description }),
    price: `${formattedPrice}${i18n.t('upsell.per_month')}`,
    unitPrice,
    description: i18n.t('upsell.plan_description'),
    plan,
    tier: plan.tier,
    name: plan.description,
    artsPerMonth: arts,
  }
}

export const PRIMEIRA20_COUPON_CODE = 'PRIMEIRA20'
export const DEFAULT_PROMO_DISCOUNT_RATE = 0.20408163

export function buildMarketComparisonPrice(
  pkg,
  {
    language = getStripeLanguage(),
    multiplier = 10,
  } = {},
) {
  const priceValue = getPackagePriceValue(pkg)
  if (priceValue == null) return null

  return formatUpsellStaticCurrency(
    priceValue * multiplier,
    language,
    pkg?.currency,
  )
}

export function buildPlanMarketComparisonPrice(
  plan,
  {
    language = getStripeLanguage(),
    multiplier = 15,
  } = {},
) {
  if (!plan?.price) return null

  return formatUpsellPrice(
    plan.price * multiplier,
    language,
    plan.currency,
  )
}

export function buildCouponPackageOffer(
  pkg,
  {
    language = getStripeLanguage(),
    couponCode = PRIMEIRA20_COUPON_CODE,
    couponText,
    discountRate = DEFAULT_PROMO_DISCOUNT_RATE,
    timerSeconds = 900,
    description,
  } = {},
) {
  const priceValue = getPackagePriceValue(pkg)
  if (priceValue == null) return null

  const discountedValue = Number((priceValue * (1 - discountRate)).toFixed(2))
  const savingsValue = Number((priceValue - discountedValue).toFixed(2))

  return {
    couponCode,
    couponText:
      couponText ??
      i18n.t('upsell.coupon_text', { coupon: couponCode }),
    originalPrice: getPackagePrice(pkg, language),
    price: formatUpsellStaticCurrency(discountedValue, language, pkg?.currency),
    priceValue: discountedValue,
    originalPriceValue: priceValue,
    savings: formatUpsellStaticCurrency(savingsValue, language, pkg?.currency),
    savingsValue,
    timerSeconds,
    description,
  }
}

export function buildDownloadOffer(
  pkg,
  {
    language = getStripeLanguage(),
    label,
    description,
  } = {},
) {
  if (!pkg) return null

  return {
    label: label ?? i18n.t('upsell.download_offer.label'),
    price: getPackagePrice(pkg, language),
    priceValue: getPackagePriceValue(pkg),
    description: description ?? i18n.t('upsell.download_offer.description'),
    package: pkg,
    code: pkg.code,
  }
}
